import React, {useState, useEffect, useMemo} from 'react';
import {ActivityIndicator, TouchableOpacity, View, TextInput, Text} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import Ionicons from 'react-native-vector-icons/Ionicons';
import {Chess, validateFen} from 'chess.js';
import {Chessboard} from 'react-chessboard';
import SlideModal from '@/Atoms/SlideModal';
import Loader from '@/Atoms/CCLoader/CCLoader';
import useCreateStudyGame from '@/Hooks/CreateNewStudyApi/useCreateStudyGame';
import useCreateNewChapter from '@/Hooks/CreateNewChapterApi/useCreateNewChapter';
import useChessboardColorTheme from '@/Hooks/ChessboardColorTheme/useChessboardColorTheme';
import {useNavigation} from '@react-navigation/native';
import {useToast} from '@/toast/useToast';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import {ScreenName} from '@/navigators/StackConstants';
import {ensureBlankLineAfterHeaders} from '@/Utils/CommonUtils';
import {newChapterDetailsType} from './types';

const START_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';

function splitPgn(pgnText: string): string[] {
  const games: string[] = [];
  const lines = pgnText.replace(/\r\n/g, '\n').split('\n');
  let currentGame: string[] = [];
  let inMoveSection = false;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Check if it is a valid PGN tag line: [Key "Value"]
    const isTagLine = /^\[[A-Za-z0-9_]+\s+"[^"]*"\]$/.test(trimmed);

    if (isTagLine) {
      if (inMoveSection) {
        const gameStr = currentGame.join('\n').trim();
        if (gameStr) {
          games.push(gameStr);
        }
        currentGame = [];
        inMoveSection = false;
      }
    } else if (trimmed !== '') {
      inMoveSection = true;
    }

    currentGame.push(line);
  }

  if (currentGame.length > 0) {
    const gameStr = currentGame.join('\n').trim();
    if (gameStr) {
      games.push(gameStr);
    }
  }

  return games;
}

function getPgnGameName(
  gamePgn: string,
  index: number,
  fallbackBase: string,
): string {
  const getHeader = (key: string) => {
    const match = gamePgn.match(new RegExp(`\\[${key}\\s+"([^"]+)"\\]`, 'i'));
    return match ? match[1].trim() : null;
  };

  const event = getHeader('Event');
  const white = getHeader('White');
  const black = getHeader('Black');

  const hasWhite = white && white !== '?';
  const hasBlack = black && black !== '?';

  if (hasWhite && hasBlack) {
    return `${white} vs ${black}`;
  }
  if (hasWhite) {
    return white;
  }
  if (hasBlack) {
    return black;
  }

  if (
    event &&
    event !== '?' &&
    event !== 'Lichess Study' &&
    event !== 'Lichess Game'
  ) {
    return event;
  }

  return fallbackBase
    ? `${fallbackBase} - Game ${index + 1}`
    : `Game ${index + 1}`;
}

function replaceEventHeader(pgn: string, newName: string): string {
  const eventRegex = /^\[Event\s+"[^"]*"\]/im;
  if (eventRegex.test(pgn)) {
    return pgn.replace(eventRegex, `[Event "${newName}"]`);
  } else {
    return `[Event "${newName}"]\n${pgn}`;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// BoardEditor Component (Popup 2)
// ─────────────────────────────────────────────────────────────────────────────
function BoardEditor({
  initialFen,
  onApply,
  onClose,
}: {
  initialFen: string;
  onApply: (fen: string) => void;
  onClose: () => void;
}) {
  const chessboardColorTheme = useChessboardColorTheme();

  const squareColors = useMemo(() => {
    if (chessboardColorTheme === 'green-white') {
      return {light: '#EBECD0', dark: '#739552'};
    } else if (chessboardColorTheme === 'blue-white') {
      return {light: '#DFDFDF', dark: '#6F84B4'};
    } else {
      return {light: '#edd899', dark: '#d1ab41'};
    }
  }, [chessboardColorTheme]);

  const [editorChess] = useState(() => {
    const c = new Chess();
    if (initialFen) {
      try {
        c.load(initialFen);
      } catch (e) {}
    }
    return c;
  });
  const [fen, setFen] = useState(editorChess.fen());
  const [editorError, setEditorError] = useState('');
  const [selectedPiece, setSelectedPiece] = useState<{type: string; color: string} | null>(null);
  const [eraserActive, setEraserActive] = useState(false);
  const [turn, setTurn] = useState<'w' | 'b'>('w');

  const [wk, setWk] = useState(true);
  const [wq, setWq] = useState(true);
  const [bk, setBk] = useState(true);
  const [bq, setBq] = useState(true);

  useEffect(() => {
    if (initialFen) {
      const parts = initialFen.split(' ');
      if (parts.length > 1) {
        setTurn(parts[1] as any);
      }
      if (parts.length > 2) {
        const cRights = parts[2];
        setWk(cRights.includes('K'));
        setWq(cRights.includes('Q'));
        setBk(cRights.includes('k'));
        setBq(cRights.includes('q'));
      }
    }
  }, [initialFen]);

  const updateFenState = () => {
    const position = editorChess.fen().split(' ')[0];
    // The wk/wq/bk/bq checkboxes only record the coach's *intent* - they
    // don't get cleared when a king or rook is dragged/erased off its home
    // square, so a right could end up claimed here for a rook that's no
    // longer on the board. chess.js's own validateFen doesn't catch that,
    // but a stricter validator (python-chess, used server-side for
    // practice_start_fen) rejects it as an illegal position. Only emit a
    // right when the piece it depends on is actually still in place, so the
    // editor can never produce that.
    const hasPieceAt = (square: string, type: string, color: 'w' | 'b') => {
      const piece = editorChess.get(square as any);
      return !!piece && piece.type === type && piece.color === color;
    };
    const whiteKingHome = hasPieceAt('e1', 'k', 'w');
    const blackKingHome = hasPieceAt('e8', 'k', 'b');
    let castling = '';
    if (wk && whiteKingHome && hasPieceAt('h1', 'r', 'w')) castling += 'K';
    if (wq && whiteKingHome && hasPieceAt('a1', 'r', 'w')) castling += 'Q';
    if (bk && blackKingHome && hasPieceAt('h8', 'r', 'b')) castling += 'k';
    if (bq && blackKingHome && hasPieceAt('a8', 'r', 'b')) castling += 'q';
    if (!castling) castling = '-';

    const newFen = `${position} ${turn} ${castling} - 0 1`;
    // Board interactions (click-to-place, drag-drop, erase, clear) mutate
    // editorChess's board directly via put()/remove(), which never validate
    // - a removed king is really gone from _board. So run the same
    // validateFen check the manual FEN textbox uses below, instead of just
    // try/catching load() and swallowing the error: load() validates before
    // touching _board, so a caught failure here would otherwise silently
    // drop the error and leave "Apply Position" enabled on an illegal king
    // count.
    const check = validateFen(newFen);
    if (!check.ok) {
      setFen(newFen);
      setEditorError(check.error);
      return;
    }
    editorChess.load(newFen);
    setFen(newFen);
    setEditorError('');
  };

  const handleSquareClick = (square: string) => {
    if (eraserActive) {
      editorChess.remove(square as any);
    } else if (selectedPiece) {
      editorChess.put(
        {type: selectedPiece.type as any, color: selectedPiece.color as any},
        square as any,
      );
    }
    updateFenState();
  };

  const handlePieceDrop = (
    sourceSquare: string,
    targetSquare: string,
    piece: string,
  ) => {
    editorChess.remove(sourceSquare as any);
    if (targetSquare) {
      const color = piece[0] === 'w' ? 'w' : 'b';
      const type = piece[1].toLowerCase();
      editorChess.put({type: type as any, color: color as any}, targetSquare as any);
    }
    updateFenState();
    return true;
  };

  const handleClearBoard = () => {
    editorChess.clear();
    updateFenState();
  };

  const handleResetBoard = () => {
    editorChess.load(START_FEN);
    setTurn('w');
    setWk(true);
    setWq(true);
    setBk(true);
    setBq(true);
    updateFenState();
  };

  useEffect(() => {
    updateFenState();
  }, [turn, wk, wq, bk, bq]);

  const whitePieces = [
    {type: 'k', color: 'w', label: '♔'},
    {type: 'q', color: 'w', label: '♕'},
    {type: 'r', color: 'w', label: '♖'},
    {type: 'b', color: 'w', label: '♗'},
    {type: 'n', color: 'w', label: '♘'},
    {type: 'p', color: 'w', label: '♙'},
  ];

  const blackPieces = [
    {type: 'k', color: 'b', label: '♚'},
    {type: 'q', color: 'b', label: '♛'},
    {type: 'r', color: 'b', label: '♜'},
    {type: 'b', color: 'b', label: '♝'},
    {type: 'n', color: 'b', label: '♞'},
    {type: 'p', color: 'b', label: '♟'},
  ];

  const renderPalette = (pieces: {type: string; color: string; label: string}[]) => (
    <View style={{flexDirection: 'row', gap: 6}}>
      {pieces.map((p, idx) => {
        const isSelected =
          selectedPiece?.type === p.type &&
          selectedPiece?.color === p.color &&
          !eraserActive;
        return (
          // @ts-ignore
          <div
            key={idx}
            style={{
              width: 32,
              height: 32,
              borderRadius: 6,
              borderWidth: 1.5,
              borderStyle: 'solid',
              borderColor: isSelected ? '#B58E58' : '#E6E6E6',
              backgroundColor: isSelected ? '#FFFDF8' : '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
            onClick={() => {
              setSelectedPiece(p);
              setEraserActive(false);
            }}>
            <img
              src={`https://lichess1.org/assets/piece/cburnett/${p.color}${p.type.toUpperCase()}.svg`}
              alt={p.label}
              className="draggable-palette-piece"
              draggable={true}
              onDragStart={(e: any) => {
                e.dataTransfer.setData('text/plain', JSON.stringify(p));
              }}
              style={{
                width: '68%',
                height: '68%',
              }}
            />
          </div>
        );
      })}
    </View>
  );

  return (
    <View style={{width: '100%'}}>
      {/* CSS drag overrides */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
        .draggable-palette-piece {
          -webkit-user-drag: element !important;
          user-drag: element !important;
          cursor: grab !important;
          user-select: none !important;
          -webkit-user-select: none !important;
        }
        .draggable-palette-piece:active {
          cursor: grabbing !important;
        }
      `,
        }}
      />

      <View style={{flexDirection: 'row', width: '100%'}}>
        {/* Left Column: Board + standard/clear */}
        <View style={{padding: 24, alignItems: 'center', gap: 12, borderRightWidth: 1, borderColor: '#E6E6E6'}}>
          {/* @ts-ignore */}
          <div
            style={{
              width: 250,
              height: 250,
              borderRadius: 8,
              overflow: 'hidden',
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: 'rgba(77,63,55,0.12)',
            }}
            onDragOverCapture={(e: any) => {
              e.preventDefault();
            }}
            onDropCapture={(e: any) => {
              try {
                const rawData = e.dataTransfer.getData('text/plain');
                if (!rawData) return;
                e.preventDefault();
                e.stopPropagation();
                const p = JSON.parse(rawData);

                if (p && p.type && p.color) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const y = e.clientY - rect.top;
                  const size = rect.width;
                  const squareSize = size / 8;
                  const col = Math.floor(x / squareSize);
                  const row = Math.floor(y / squareSize);

                  if (col >= 0 && col < 8 && row >= 0 && row < 8) {
                    const file = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'][col];
                    const rank = 8 - row;
                    const square = `${file}${rank}`;

                    editorChess.put(
                      {type: p.type as any, color: p.color as any},
                      square as any,
                    );
                    updateFenState();
                  }
                }
              } catch (err) {
                console.error('Failed to drop piece:', err);
              }
            }}>
            <Chessboard
              position={fen}
              onSquareClick={handleSquareClick}
              onPieceDrop={handlePieceDrop}
              boardWidth={250}
              customLightSquareStyle={{backgroundColor: squareColors.light}}
              customDarkSquareStyle={{backgroundColor: squareColors.dark}}
              dropOffBoardAction="trash"
            />
          </div>
          
          <View style={{flexDirection: 'row', gap: 8, justifyContent: 'center', width: '100%'}}>
            <TouchableOpacity
              onPress={handleResetBoard}
              activeOpacity={0.8}
              style={{
                borderWidth: 1,
                borderColor: '#E6E6E6',
                backgroundColor: '#FFFDF8',
                borderRadius: 100,
                paddingHorizontal: 16,
                paddingVertical: 5,
              }}>
              <Text style={{fontSize: 11, fontWeight: 'bold', color: '#7A6652'}}>Standard</Text>
            </TouchableOpacity>
            
            <TouchableOpacity
              onPress={handleClearBoard}
              activeOpacity={0.8}
              style={{
                borderWidth: 1,
                borderColor: '#E6E6E6',
                backgroundColor: '#FFFDF8',
                borderRadius: 100,
                paddingHorizontal: 16,
                paddingVertical: 5,
              }}>
              <Text style={{fontSize: 11, fontWeight: 'bold', color: '#7A6652'}}>Clear</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Right Column: Controls */}
        <View style={{flex: 1, padding: 24, gap: 14}}>
          {/* Pieces grid */}
          <View style={{gap: 4}}>
            <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>PIECES</Text>
            {renderPalette(whitePieces)}
            {renderPalette(blackPieces)}
          </View>

          {/* Eraser */}
          <TouchableOpacity
            activeOpacity={0.8}
            onPress={() => {
              setSelectedPiece(null);
              setEraserActive(true);
            }}
            style={{
              height: 34,
              borderRadius: 6,
              borderWidth: 1,
              borderColor: eraserActive ? '#B58E58' : '#E6E6E6',
              backgroundColor: eraserActive ? '#FFFDF8' : '#FFFFFF',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}>
            <MaterialCommunityIcons name="eraser" size={14} color={eraserActive ? '#B58E58' : '#757575'} />
            <Text style={{fontSize: 11, fontWeight: 'bold', color: eraserActive ? '#B58E58' : '#757575'}}>Eraser</Text>
          </TouchableOpacity>

          {/* Turn */}
          <View style={{gap: 6}}>
            <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>TURN TO MOVE</Text>
            <View style={{flexDirection: 'row', backgroundColor: '#F5F5F5', borderRadius: 8, padding: 3, height: 36}}>
              <TouchableOpacity
                onPress={() => setTurn('w')}
                style={{
                  flex: 1,
                  borderRadius: 6,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: turn === 'w' ? '#FFFFFF' : 'transparent',
                  shadowColor: turn === 'w' ? '#000' : 'transparent',
                  shadowOffset: {width: 0, height: 1},
                  shadowOpacity: 0.15,
                  shadowRadius: 2,
                  elevation: 1,
                }}>
                <Text style={{fontSize: 12, fontWeight: turn === 'w' ? 'bold' : 'normal', color: turn === 'w' ? '#1E110A' : '#757575'}}>White</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={() => setTurn('b')}
                style={{
                  flex: 1,
                  borderRadius: 6,
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: turn === 'b' ? '#FFFFFF' : 'transparent',
                  shadowColor: turn === 'b' ? '#000' : 'transparent',
                  shadowOffset: {width: 0, height: 1},
                  shadowOpacity: 0.15,
                  shadowRadius: 2,
                  elevation: 1,
                }}>
                <Text style={{fontSize: 12, fontWeight: turn === 'b' ? 'bold' : 'normal', color: turn === 'b' ? '#1E110A' : '#757575'}}>Black</Text>
              </TouchableOpacity>
            </View>
          </View>

          {/* Castling rights */}
          <View style={{gap: 6}}>
            <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>CASTLING RIGHTS</Text>
            <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: 6}}>
              {[
                {label: 'W O-O', val: wk, setVal: setWk},
                {label: 'W O-O-O', val: wq, setVal: setWq},
                {label: 'B O-O', val: bk, setVal: setBk},
                {label: 'B O-O-O', val: bq, setVal: setBq},
              ].map((c, i) => (
                <TouchableOpacity
                  key={i}
                  activeOpacity={0.8}
                  onPress={() => c.setVal(!c.val)}
                  style={{
                    paddingHorizontal: 10,
                    paddingVertical: 6,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: c.val ? '#B58E58' : '#E6E6E6',
                    backgroundColor: c.val ? '#FFFDF8' : '#FFFFFF',
                  }}>
                  <Text
                    style={{
                      fontSize: 10,
                      fontWeight: 'bold',
                      color: c.val ? '#B58E58' : '#757575',
                    }}>
                    {c.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>

          {/* Resulting FEN */}
          <View style={{gap: 6}}>
            <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>RESULTING FEN</Text>
            <TextInput
              value={fen}
              onChangeText={text => {
                setFen(text);
                const check = validateFen(text);
                if (!check.ok) {
                  setEditorError(check.error);
                } else {
                  setEditorError('');
                  try {
                    editorChess.load(text);
                    const parts = text.split(' ');
                    if (parts.length > 1) setTurn(parts[1] as any);
                    if (parts.length > 2) {
                      const cRights = parts[2];
                      setWk(cRights.includes('K'));
                      setWq(cRights.includes('Q'));
                      setBk(cRights.includes('k'));
                      setBq(cRights.includes('q'));
                    }
                  } catch (e) {}
                }
              }}
              style={{
                height: 38,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: editorError ? '#FF4D4D' : '#E6E6E6',
                backgroundColor: '#F9F9F9',
                paddingHorizontal: 10,
                fontSize: 10.5,
                color: '#555555',
              }}
            />
            {!!editorError && (
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 6,
                  paddingVertical: 6,
                  paddingHorizontal: 10,
                  borderRadius: 8,
                  backgroundColor: '#FFF1F0',
                  borderWidth: 1,
                  borderColor: '#FFCCC7',
                }}>
                <Ionicons name="alert-circle" size={13} color="#FF4D4D" />
                <Text
                  style={{
                    fontSize: 10.5,
                    color: '#D93025',
                    fontWeight: '600',
                    flex: 1,
                  }}>
                  {editorError}
                </Text>
              </View>
            )}
          </View>
        </View>
      </View>

      {/* Footer */}
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'flex-end',
          gap: 10,
          paddingHorizontal: 24,
          paddingBottom: 20,
          paddingTop: 10,
          borderTopWidth: 1,
          borderColor: '#F5F5F5',
        }}>
        <TouchableOpacity
          onPress={onClose}
          activeOpacity={0.8}
          style={{
            borderWidth: 1.5,
            borderColor: '#CCCCCC',
            backgroundColor: '#FFFFFF',
            borderRadius: 100,
            paddingHorizontal: 22,
            paddingVertical: 8,
          }}>
          <Text style={{fontSize: 12.5, fontWeight: 'bold', color: '#2E1B10'}}>Cancel</Text>
        </TouchableOpacity>

        <TouchableOpacity
          disabled={!!editorError}
          onPress={() => onApply(fen)}
          activeOpacity={0.8}
          style={{
            backgroundColor: '#FACF47',
            borderRadius: 100,
            paddingHorizontal: 24,
            paddingVertical: 8,
            opacity: editorError ? 0.5 : 1,
          }}>
          <Text style={{fontSize: 12.5, fontWeight: 'bold', color: '#1E110A'}}>Apply Position</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main CreateNewStudy Component (Popup 1)
// ─────────────────────────────────────────────────────────────────────────────
function CreateNewStudy({
  createChapterOnly = false,
  successCallback,
  overrideButton,
  studySlug,
  setNewChapterCreated,
  isStudyModalOpen,
  setIsStudyModalOpen,
}: {
  createChapterOnly?: boolean;
  successCallback?: () => void;
  overrideButton?: (func: () => void) => any;
  studySlug?: string;
  setNewChapterCreated?: (func: () => void) => any;
  isStudyModalOpen: boolean;
  setIsStudyModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const navigation = useNavigation();
  const showToast = useToast();
  const {isMobileOrTab} = useMediaQuery();
  const [openNewStudyModal, setOpenNewStudyModal] = useState(false);
  const [isBoardEditorVisible, setIsBoardEditorVisible] = useState(false);
  const [error, setError] = useState({flag: false, errorMsg: ''});

  const [newChapterDetails, setNewChapterDetails] = useState<newChapterDetailsType>({
    name: '',
    types: {key: 'Start', value: 'Start'}, // Default to start position
    fen: START_FEN,
    pgn: '',
  });

  const [orientation, setOrientation] = useState<'White' | 'Black'>('White');
  const [newStudyDetails, setNewStudyDetails] = useState<{
    name: string;
    chapterName: string;
    pgn: string;
    type:
      | {key: 'FEN'; value: 'FEN'}
      | {key: 'PGN'; value: 'PGN'}
      | {key: 'Start'; value: 'Start'}
      | null;
  }>({
    name: '',
    pgn: '',
    type: null,
  });

  useEffect(() => {
    setOpenNewStudyModal(isStudyModalOpen);
  }, [isStudyModalOpen]);

  const {
    mutateAsync: createNewStudyAsync,
    data: newStudySuccess,
    error: newStudyError,
  } = useCreateStudyGame();

  const {
    mutateAsync: createNewChapterAsync,
    data: newChapterSuccess,
    error: newChapterError,
  } = useCreateNewChapter();

  const [loaderState, setLoaderState] = useState(false);

  useEffect(() => {
    if (newStudyError || newChapterError) {
      setError({flag: true, errorMsg: ''});
      setLoaderState(false);
    }
  }, [newStudyError, newChapterError]);

  useEffect(() => {
    if (newStudySuccess?.success) {
      handleNewStudy(newStudySuccess);
      setLoaderState(false);
    } else if (newChapterSuccess?.success) {
      successCallback?.();
      setLoaderState(false);
      setNewChapterCreated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newStudySuccess, newChapterSuccess]);

  const handleNewStudy = newGameDetails => {
    const studyGameSlug = newGameDetails?.data?.slug;
    navigation.navigate(ScreenName.StudyDetails, {
      study_id: studyGameSlug,
    });
  };

  const resetStates = () => {
    setOpenNewStudyModal(false);
    setIsStudyModalOpen(false);
    setError({flag: false, errorMsg: ''});
    setNewChapterDetails({
      name: '',
      types: {key: 'Start', value: 'Start'},
      fen: START_FEN,
      pgn: '',
    });
    setOrientation('White');
    setNewStudyDetails({
      name: '',
      pgn: '',
      type: null,
    });
  };

  const handleFenSubmit = async () => {
    try {
      setLoaderState(true);
      setError({flag: false, errorMsg: ''});

      const typesKey = newChapterDetails?.types?.key;

      if (typesKey === 'FEN' || typesKey === 'Editor') {
        const validatePiece = validateFen(newChapterDetails?.fen);
        if (!validatePiece?.ok) {
          throw new Error(validatePiece?.error);
        }

        const startingPos = {fen: newChapterDetails?.fen};
        if (createChapterOnly) {
          await createNewChapterAsync({
            name: newChapterDetails?.name,
            ...startingPos,
            orientation: orientation === 'Black' ? 'B' : 'W',
            slug: studySlug,
          });
          successCallback?.();
          setLoaderState(false);
          setNewChapterCreated(true);
          resetStates();
        } else {
          const res = await createNewStudyAsync({
            name: newStudyDetails?.name,
            chapter: {
              name: newChapterDetails?.name,
              ...startingPos,
              orientation: orientation === 'Black' ? 'B' : 'W',
            },
          });
          if (res?.success) {
            handleNewStudy(res);
          }
          setLoaderState(false);
          resetStates();
        }
      } else if (typesKey === 'PGN') {
        const pgn = newChapterDetails?.pgn;
        if (!pgn || typeof pgn !== 'string') {
          throw new Error('PGN is empty or not a string.');
        }

        const games = splitPgn(pgn);
        if (games.length === 0) {
          throw new Error('No valid games found in PGN.');
        }

        if (createChapterOnly) {
          const failedGameNumbers: number[] = [];
          for (let i = 0; i < games.length; i++) {
            const gamePgn = games[i];
            const formattedPgn = ensureBlankLineAfterHeaders(gamePgn);

            try {
              new Chess().loadPgn(formattedPgn);
            } catch (err) {
              console.error(`Failed parsing game ${i + 1}:`, err);
              failedGameNumbers.push(i + 1);
              continue;
            }

            const chapterName = getPgnGameName(formattedPgn, i, newChapterDetails?.name);
            const pgnWithEventName = replaceEventHeader(formattedPgn, chapterName);

            await createNewChapterAsync({
              name: chapterName,
              pgn: pgnWithEventName,
              orientation: orientation === 'Black' ? 'B' : 'W',
              slug: studySlug,
            });
          }
          if (failedGameNumbers.length === games.length) {
            throw new Error('None of the games could be parsed as valid PGN.');
          }
          successCallback?.();
          setLoaderState(false);
          setNewChapterCreated(true);
          resetStates();
          if (failedGameNumbers.length > 0) {
            showToast(
              `${failedGameNumbers.length} of ${games.length} game(s) skipped due to invalid PGN`,
              {type: 'Error'},
            );
          }
        } else {
          const firstGamePgn = games[0];
          const formattedFirstPgn = ensureBlankLineAfterHeaders(firstGamePgn);
          try {
            new Chess().loadPgn(formattedFirstPgn);
          } catch {
            throw new Error('The first game could not be parsed as valid PGN.');
          }
          const firstChapterName = getPgnGameName(
            formattedFirstPgn,
            0,
            newChapterDetails?.name || newStudyDetails?.name,
          );
          const pgnWithFirstEvent = replaceEventHeader(formattedFirstPgn, firstChapterName);

          const res = await createNewStudyAsync({
            name: newStudyDetails?.name,
            chapter: {
              name: firstChapterName,
              pgn: pgnWithFirstEvent,
              orientation: orientation === 'Black' ? 'B' : 'W',
            },
          });

          const studyGameSlug = res?.data?.slug;
          const failedGameNumbers: number[] = [];
          if (studyGameSlug) {
            for (let i = 1; i < games.length; i++) {
              const gamePgn = games[i];
              const formattedPgn = ensureBlankLineAfterHeaders(gamePgn);

              try {
                new Chess().loadPgn(formattedPgn);
              } catch (err) {
                console.error(`Failed parsing game ${i + 1}:`, err);
                failedGameNumbers.push(i + 1);
                continue;
              }

              const chapterName = getPgnGameName(
                formattedPgn,
                i,
                newChapterDetails?.name || newStudyDetails?.name,
              );
              const pgnWithEventName = replaceEventHeader(formattedPgn, chapterName);

              await createNewChapterAsync({
                name: chapterName,
                pgn: pgnWithEventName,
                orientation: orientation === 'Black' ? 'B' : 'W',
                slug: studyGameSlug,
              });
            }
            handleNewStudy(res);
          }
          setLoaderState(false);
          resetStates();
          if (failedGameNumbers.length > 0) {
            showToast(
              `${failedGameNumbers.length} of ${games.length - 1} additional game(s) skipped due to invalid PGN`,
              {type: 'Error'},
            );
          }
        }
      } else {
        const startingPos = {fen: START_FEN};
        if (createChapterOnly) {
          await createNewChapterAsync({
            name: newChapterDetails?.name,
            ...startingPos,
            orientation: orientation === 'Black' ? 'B' : 'W',
            slug: studySlug,
          });
          successCallback?.();
          setLoaderState(false);
          setNewChapterCreated(true);
          resetStates();
        } else {
          const res = await createNewStudyAsync({
            name: newStudyDetails?.name,
            chapter: {
              name: newChapterDetails?.name,
              ...startingPos,
              orientation: orientation === 'Black' ? 'B' : 'W',
            },
          });
          if (res?.success) {
            handleNewStudy(res);
          }
          setLoaderState(false);
          resetStates();
        }
      }
    } catch (err) {
      setLoaderState(false);
      setError({flag: true, errorMsg: `${err}`});
    }
  };

  const nameLen = newStudyDetails?.name?.length || 0;
  const showNameHint = !createChapterOnly && nameLen > 0 && nameLen < 4;
  const typeKey = newChapterDetails?.types?.key;

  const canSubmit =
    (createChapterOnly || nameLen >= 4) &&
    !!newChapterDetails?.name &&
    !(typeKey === 'FEN' && !newChapterDetails?.fen) &&
    !(typeKey === 'Editor' && !newChapterDetails?.fen) &&
    !(typeKey === 'PGN' && !newChapterDetails?.pgn);

  const previewDescription = useMemo(() => {
    switch (typeKey) {
      case 'FEN':
        return 'Custom FEN position';
      case 'Editor':
        return 'Custom board position';
      case 'PGN':
        return 'PGN game moves';
      default:
        return 'Standard starting position';
    }
  }, [typeKey]);

  return (
    <>
      <Loader loading={loaderState} />
      
      {/* ── Popup 1: New Workbook/Chapter Modal ─────────────────────────────── */}
      <SlideModal
        visible={openNewStudyModal}
        onClose={resetStates}
        type="center"
        showHeader={false}
        contentPadding={0}
        customStyle={{
          borderRadius: 20,
          overflow: 'hidden',
          backgroundColor: '#FFFFFF',
          width: 660,
          maxWidth: '92%',
          alignSelf: 'center',
          borderWidth: 0,
          shadowColor: '#000',
          shadowOffset: {width: 0, height: 10},
          shadowOpacity: 0.08,
          shadowRadius: 20,
          elevation: 5,
        }}>
        
        {/* Header Block */}
        <View
          style={{
            backgroundColor: '#2C1A11',
            paddingHorizontal: 24,
            paddingVertical: 18,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
          }}>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, marginRight: 24}}>
            <View
              style={{
                width: 38,
                height: 38,
                borderRadius: 8,
                backgroundColor: '#402C1F',
                borderWidth: 1,
                borderColor: '#B58E58',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <MaterialCommunityIcons name="chess-king" size={18} color="#FFFFFF" />
            </View>
            <View style={{flex: 1}}>
              <Text style={{fontSize: 15, fontWeight: 'bold', color: '#FFFFFF', letterSpacing: -0.2}}>
                {createChapterOnly ? 'New Chapter' : 'New Workbook'}
              </Text>
              <Text style={{fontSize: 11, color: '#A39081', marginTop: 1}}>
                {createChapterOnly
                  ? 'A fresh board, appended after your existing chapters.'
                  : "Name it, set the position, and you're in."}
              </Text>
            </View>
          </View>

          {/* Close button */}
          <TouchableOpacity
            onPress={resetStates}
            activeOpacity={0.8}
            style={{
              width: 28,
              height: 28,
              borderRadius: 14,
              backgroundColor: 'rgba(255,255,255,0.08)',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Ionicons name="close-outline" size={16} color="#FFFFFF" />
          </TouchableOpacity>
        </View>

        {/* Body columns */}
        <View style={{flexDirection: 'row', width: '100%'}}>
          {/* Left Column: Form Fields */}
          <View style={{flex: 1.2, padding: 24, gap: 14}}>
            {/* Inputs row */}
            <View style={{flexDirection: 'row', gap: 10, width: '100%'}}>
              {!createChapterOnly && (
                <View style={{flex: 1, gap: 6}}>
                  <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>WORKBOOK NAME</Text>
                  <TextInput
                    value={newStudyDetails?.name}
                    placeholder="e.g. Sicilian Najdorf"
                    placeholderTextColor="#A39081"
                    onChangeText={text => setNewStudyDetails(prev => ({...prev, name: text}))}
                    style={{
                      height: 38,
                      borderRadius: 8,
                      borderWidth: 1,
                      borderColor: showNameHint ? '#FF4D4D' : '#E6E6E6',
                      backgroundColor: '#FFFFFF',
                      paddingHorizontal: 12,
                      fontSize: 12,
                      color: '#1E110A',
                    }}
                  />
                  {showNameHint && (
                    <Text style={{fontSize: 9, color: '#FF4D4D', fontWeight: '500'}}>Minimum 4 characters required</Text>
                  )}
                </View>
              )}
              <View style={{flex: 1, gap: 6}}>
                <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>CHAPTER NAME</Text>
                <TextInput
                  value={newChapterDetails?.name}
                  placeholder="e.g. Main line"
                  placeholderTextColor="#A39081"
                  onChangeText={text => setNewChapterDetails(prev => ({...prev, name: text}))}
                  style={{
                    height: 38,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: '#E6E6E6',
                    backgroundColor: '#FFFFFF',
                    paddingHorizontal: 12,
                    fontSize: 12,
                    color: '#1E110A',
                  }}
                />
              </View>
            </View>

            {/* Orientation */}
            <View style={{gap: 6}}>
              <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>ORIENTATION</Text>
              <View style={{flexDirection: 'row', backgroundColor: '#F5F5F5', borderRadius: 8, padding: 3, height: 36}}>
                {(['White', 'Black'] as const).map(o => (
                  <TouchableOpacity
                    key={o}
                    onPress={() => setOrientation(o)}
                    style={{
                      flex: 1,
                      borderRadius: 6,
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: orientation === o ? '#FFFFFF' : 'transparent',
                      shadowColor: orientation === o ? '#000' : 'transparent',
                      shadowOffset: {width: 0, height: 1},
                      shadowOpacity: 0.15,
                      shadowRadius: 2,
                      elevation: 1,
                    }}>
                    <Text style={{fontSize: 12, fontWeight: orientation === o ? 'bold' : 'normal', color: orientation === o ? '#1E110A' : '#757575'}}>{o}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Position Selector cards */}
            <View style={{gap: 6}}>
              <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>POSITION</Text>
              <View style={{flexDirection: 'row', justifyContent: 'space-between', gap: 6}}>
                {[
                  {key: 'Start', label: 'Start', icon: 'chess-king'},
                  {key: 'FEN', label: 'FEN', icon: 'checkerboard'},
                  {key: 'Editor', label: 'Editor', icon: 'chess-pawn'},
                  {key: 'PGN', label: 'PGN', icon: 'file-document-outline'},
                ].map(item => {
                  const isActive = typeKey === item.key;
                  return (
                    <TouchableOpacity
                      key={item.key}
                      activeOpacity={0.8}
                      onPress={() => {
                        setNewChapterDetails(prev => ({
                          ...prev,
                          types: {key: item.key as any, value: item.key as any},
                        }));
                        if (item.key === 'Editor') setIsBoardEditorVisible(true);
                      }}
                      style={{
                        flex: 1,
                        borderRadius: 10,
                        borderWidth: 1,
                        borderColor: isActive ? '#B58E58' : '#E6E6E6',
                        backgroundColor: isActive ? '#FFFDF8' : '#FFFFFF',
                        alignItems: 'center',
                        justifyContent: 'center',
                        paddingVertical: 8,
                        gap: 4,
                      }}>
                      <MaterialCommunityIcons name={item.icon} size={15} color={isActive ? '#B58E58' : '#757575'} />
                      <Text style={{fontSize: 10, fontWeight: 'bold', color: isActive ? '#B58E58' : '#757575'}}>{item.label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>

            {/* FEN/PGN Input box if selected */}
            {typeKey === 'FEN' && (
              <View style={{gap: 6}}>
                <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>FEN STRING</Text>
                <TextInput
                  value={newChapterDetails?.fen}
                  placeholder="rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
                  placeholderTextColor="#A39081"
                  onChangeText={text => {
                    setNewChapterDetails(prev => ({...prev, fen: text}));
                    if (text) {
                      const check = validateFen(text);
                      setError(check.ok ? {flag: false, errorMsg: ''} : {flag: true, errorMsg: check.error});
                    } else {
                      setError({flag: false, errorMsg: ''});
                    }
                  }}
                  style={{
                    height: 38,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: error?.flag ? '#FF4D4D' : '#E6E6E6',
                    backgroundColor: '#FFFFFF',
                    paddingHorizontal: 12,
                    fontSize: 11,
                    color: '#1E110A',
                  }}
                />
                {error?.flag && (
                  <Text style={{fontSize: 9, color: '#FF4D4D', fontWeight: '500'}}>{error?.errorMsg}</Text>
                )}
              </View>
            )}

            {typeKey === 'PGN' && (
              <View style={{gap: 6}}>
                <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>PGN CONTENT</Text>
                <TextInput
                  value={newChapterDetails?.pgn}
                  placeholder="1. e4 e5 2. Nf3 Nc6..."
                  placeholderTextColor="#A39081"
                  multiline
                  numberOfLines={3}
                  onChangeText={text => {
                    setNewChapterDetails(prev => ({...prev, pgn: text}));
                    setError({flag: false, errorMsg: ''});
                  }}
                  style={{
                    height: 60,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderColor: '#E6E6E6',
                    backgroundColor: '#FFFFFF',
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                    fontSize: 11,
                    color: '#1E110A',
                  }}
                />
              </View>
            )}
          </View>

          {/* Divider */}
          <View style={{width: 1, backgroundColor: '#E6E6E6'}} />

          {/* Right Column: Preview Board */}
          <View style={{width: 230, backgroundColor: '#FFFDF6', padding: 20, alignItems: 'center', justifyContent: 'center', gap: 10}}>
            <Text style={{fontSize: 10, fontWeight: 'bold', color: '#9E9E9E', letterSpacing: 0.5}}>PREVIEW</Text>
            
            <View style={{width: 170, height: 170, borderRadius: 8, overflow: 'hidden', borderWidth: 1, borderColor: '#EAE2D5'}}>
              <Chessboard
                position={typeKey === 'FEN' || typeKey === 'Editor' ? newChapterDetails?.fen || START_FEN : START_FEN}
                boardWidth={170}
                customLightSquareStyle={{backgroundColor: '#edd899'}}
                customDarkSquareStyle={{backgroundColor: '#d1ab41'}}
                arePiecesDraggable={false}
              />
            </View>

            <Text style={{fontSize: 10, color: '#999999', marginTop: 4}}>{previewDescription}</Text>
          </View>
        </View>

        {/* Footer */}
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'flex-end',
            gap: 10,
            paddingHorizontal: 24,
            paddingBottom: 20,
            paddingTop: 10,
            borderTopWidth: 1,
            borderColor: '#F5F5F5',
          }}>
          <TouchableOpacity
            onPress={resetStates}
            activeOpacity={0.8}
            style={{
              borderWidth: 1.5,
              borderColor: '#CCCCCC',
              backgroundColor: '#FFFFFF',
              borderRadius: 100,
              paddingHorizontal: 22,
              paddingVertical: 8,
            }}>
            <Text style={{fontSize: 12, fontWeight: 'bold', color: '#2E1B10'}}>Cancel</Text>
          </TouchableOpacity>

          <TouchableOpacity
            disabled={!canSubmit}
            onPress={handleFenSubmit}
            activeOpacity={0.8}
            style={{
              backgroundColor: canSubmit ? '#FACF47' : '#F5F5F5',
              borderRadius: 100,
              paddingHorizontal: 24,
              paddingVertical: 8,
            }}>
            <Text style={{fontSize: 12, fontWeight: 'bold', color: canSubmit ? '#1E110A' : '#B0B0B0'}}>
              {createChapterOnly ? 'Create Chapter' : 'Create Workbook'}
            </Text>
          </TouchableOpacity>
        </View>
      </SlideModal>

      {/* ── Popup 2: Board Editor Modal ─────────────────────────────────────── */}
      <SlideModal
        visible={isBoardEditorVisible}
        onClose={() => setIsBoardEditorVisible(false)}
        type="center"
        showHeader={false}
        contentPadding={0}
        customStyle={{
          borderRadius: 20,
          overflow: 'hidden',
          backgroundColor: '#FFFFFF',
          width: 660,
          maxWidth: '92%',
          alignSelf: 'center',
          borderWidth: 0,
          shadowColor: '#000',
          shadowOffset: {width: 0, height: 10},
          shadowOpacity: 0.08,
          shadowRadius: 20,
          elevation: 5,
        }}>
        
        {/* Header Block */}
        <View
          style={{
            backgroundColor: '#2C1A11',
            paddingHorizontal: 24,
            paddingVertical: 18,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
          }}>
          <View style={{flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, marginRight: 24}}>
            <View
              style={{
                width: 38,
                height: 38,
                borderRadius: 8,
                backgroundColor: '#402C1F',
                borderWidth: 1,
                borderColor: '#B58E58',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <MaterialCommunityIcons name="chess-bishop" size={18} color="#FFFFFF" />
            </View>
            <View style={{flex: 1}}>
              <Text style={{fontSize: 15, fontWeight: 'bold', color: '#FFFFFF', letterSpacing: -0.2}}>
                Board Editor
              </Text>
              <Text style={{fontSize: 11, color: '#A39081', marginTop: 1}}>
                Click a piece, then click a square to place it.
              </Text>
            </View>
          </View>

          {/* Close button */}
          <TouchableOpacity
            onPress={() => setIsBoardEditorVisible(false)}
            activeOpacity={0.8}
            style={{
              width: 28,
              height: 28,
              borderRadius: 14,
              backgroundColor: 'rgba(255,255,255,0.08)',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
            <Ionicons name="close-outline" size={16} color="#FFFFFF" />
          </TouchableOpacity>
        </View>

        <BoardEditor
          initialFen={newChapterDetails?.fen}
          onApply={fen => {
            setNewChapterDetails(prev => ({...prev, fen}));
            setIsBoardEditorVisible(false);
          }}
          onClose={() => setIsBoardEditorVisible(false)}
        />
      </SlideModal>

      {/* Start Button Trigger */}
      <View style={{flexDirection: 'row', justifyContent: 'flex-end', marginTop: 10}}>
        {overrideButton ? (
          overrideButton(() => setOpenNewStudyModal(true))
        ) : (
          <TouchableOpacity
            activeOpacity={0.8}
            onPress={() => {
              setOpenNewStudyModal(true);
              setIsStudyModalOpen(true);
            }}
            style={{
              backgroundColor: CCColors.Primary.Action,
              paddingHorizontal: 16,
              paddingVertical: 10,
              borderRadius: 8,
            }}>
            <Text style={{fontSize: 13, fontWeight: 'bold', color: CCColors.Primary.Dark}}>
              + Start new workbook
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </>
  );
}

export default CreateNewStudy;
